import { selectLightweightElement } from "@/store/lightweight-elements-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  BackgroundTaskBase,
  getErrorCodeAndTaskName,
} from "@/utils/background-tasks";
import { FaroButton, FaroText, FontWeights, grey } from "@faro-lotv/flat-ui";
import { taskErrorToUserMessage } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { ReactNode, useState, type JSX } from "react";
import { taskErrorDetails } from "./task-error-details";

type CardErrorProps = {
  /** Task to show the error of */
  task: BackgroundTaskBase;

  /** Optional hint to provide more details to the user */
  hint?: ReactNode;

  /** Whether to show detailed error information */
  showDetails?: boolean;
};

/** @returns error message display for a failed task */
export function CardError({
  task,
  hint,
  showDetails = false,
}: CardErrorProps): JSX.Element {
  const element = useAppSelector(
    selectLightweightElement(task.iElementId ?? undefined),
  );
  const { taskName, errorCode } = getErrorCodeAndTaskName(task, element?.name);
  const errorMessage = taskErrorToUserMessage(taskName, errorCode);
  const errorDetailsContent = showDetails ? taskErrorDetails(task) : undefined;

  if (hint || errorDetailsContent) {
    return (
      <ErrorMessageWithDetails
        message={errorMessage}
        hint={hint}
        errorDetails={errorDetailsContent}
      />
    );
  }

  return (
    <FaroText variant="inlineError" fontWeight={FontWeights.Bold}>
      {errorMessage}
    </FaroText>
  );
}

type ErrorMessageWithDetailsProps = {
  /** The main error message to show */
  message: ReactNode;

  /** A resolution hint to provide more details to the user */
  hint?: ReactNode;

  /** More details about the error, that are initially collapsed */
  errorDetails?: ReactNode;
};

/** @returns error message with expandable details section */
export function ErrorMessageWithDetails({
  message,
  hint,
  errorDetails,
}: ErrorMessageWithDetailsProps): JSX.Element {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <FaroText variant="inlineError" fontWeight={FontWeights.Bold}>
          {message}
        </FaroText>
        {errorDetails && (
          <FaroButton
            variant="ghost"
            size="xs"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? "Hide" : "Show"} Details
          </FaroButton>
        )}
      </Stack>

      {hint && (
        <FaroText variant="bodyS" color={grey[600]}>
          {hint}
        </FaroText>
      )}

      {showDetails && errorDetails}
    </>
  );
}
